import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ChevronLeft, ChevronRight, Copy, GitCompareArrows } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { VersionEntry } from '@/services/shared/versionHistoryApi'
import { copyToClipboard } from '@/utils/clipboard'
import { formatDateTime } from '@/utils/format'
import { diffJson, jsonLines, parseJsonOrNull, type JsonFieldChange, type JsonLine } from '@/utils/jsonDiff'

/** One version, prepared for its columns. */
interface Step {
  number: number
  version: VersionEntry
  json: unknown
  /** Null for the version that created the object. */
  changes: JsonFieldChange[] | null
  /** Paths the version before already had, so a new container can be told apart. */
  olderPaths: Set<string> | null
}

interface VersionColumnsProps {
  /** Newest first, as the API pages them. */
  versions: VersionEntry[]
  /** The version number of `versions[0]`. */
  newestNumber: number
}

/**
 * The versions of an object side by side, oldest on the left: each version's
 * full JSON with the lines that changed lit up, and between every two versions
 * a column listing what changed. Opens scrolled to the newest.
 */
export function VersionColumns({ versions, newestNumber }: VersionColumnsProps) {
  const { t } = useTranslation()
  const scrollerRef = useRef<HTMLDivElement>(null)

  const steps = useMemo<Step[]>(() => {
    return versions
      .map((version, i) => {
        const json = parseJsonOrNull(version.json)
        // The oldest on a page has no neighbour here; the backend rebuilds its predecessor.
        const older = parseJsonOrNull(version.previousJson ?? versions[i + 1]?.json)
        return {
          number: newestNumber - i,
          version,
          json,
          changes: older && json ? diffJson(older, json) : null,
          olderPaths: older ? new Set(jsonLines(older).map((line) => line.path)) : null,
        }
      })
      .reverse()
  }, [versions, newestNumber])

  useEffect(() => {
    const el = scrollerRef.current
    if (el) el.scrollLeft = el.scrollWidth
  }, [steps])

  const scrollByColumn = (direction: 1 | -1) =>
    scrollerRef.current?.scrollBy({ left: direction * 448, behavior: 'smooth' })

  /** Brings a changed line into view in the version it landed in. */
  const revealChange = (number: number, path: string) => {
    const line = scrollerRef.current?.querySelector<HTMLElement>(
      `[data-version="${number}"] [data-path="${CSS.escape(path)}"]`
    )
    if (!line) return
    line.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    line.animate(
      [{ outline: '2px solid var(--ad-primary)' }, { outline: '2px solid transparent' }],
      { duration: 1200 }
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <Button variant="ghost" size="icon-sm" onClick={() => scrollByColumn(-1)} aria-label={t('versionHistory.older')}>
          <ChevronLeft />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={() => scrollByColumn(1)} aria-label={t('versionHistory.newer')}>
          <ChevronRight />
        </Button>
      </div>
      <div ref={scrollerRef} className="flex snap-x snap-proximity gap-3 overflow-x-auto pb-3">
        {steps.map((step) => (
          <div key={step.number} className="contents">
            {step.changes && (
              <ChangesColumn
                from={step.number - 1}
                to={step.number}
                changes={step.changes}
                onReveal={(path) => revealChange(step.number, path)}
              />
            )}
            <JsonColumn step={step} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Column({ className, header, children }: { className?: string; header: React.ReactNode; children: React.ReactNode }) {
  return (
    <section
      className={cn(
        'flex h-[70vh] shrink-0 snap-start flex-col overflow-hidden rounded-lg border bg-(--ad-surface)',
        className
      )}
    >
      <header className="flex flex-col gap-1 border-b bg-muted/50 px-3 py-2">{header}</header>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  )
}

function ChangesColumn({
  from,
  to,
  changes,
  onReveal,
}: {
  from: number
  to: number
  changes: JsonFieldChange[]
  onReveal: (path: string) => void
}) {
  const { t } = useTranslation()
  return (
    <Column
      className="w-xs border-dashed"
      header={
        <div className="flex items-center gap-2 text-sm font-medium">
          <GitCompareArrows className="size-4 text-(--ad-text-muted)" />
          <span className="font-mono">v{from}</span>
          <ArrowRight className="size-3.5 text-(--ad-text-muted)" />
          <span className="font-mono">v{to}</span>
          <Badge variant="gray" className="ml-auto">
            {t('versionHistory.fieldsChanged', { count: changes.length })}
          </Badge>
        </div>
      }
    >
      {changes.length === 0 && (
        <p className="p-3 text-xs text-(--ad-text-muted)">{t('versionHistory.noFieldChanges')}</p>
      )}
      {changes.map((change) => (
        <button
          key={change.path}
          type="button"
          disabled={change.after === undefined}
          onClick={() => onReveal(change.path)}
          className="flex w-full flex-col gap-1 border-b px-3 py-2 text-left font-mono text-xs hover:bg-muted/50 disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span className="break-all text-(--ad-text-muted)">{change.path}</span>
          {change.before !== undefined && (
            <span className="rounded-sm bg-(--ad-danger-soft) px-1 break-all text-(--ad-danger) line-through">
              {change.before}
            </span>
          )}
          {change.after !== undefined && (
            <span className="rounded-sm bg-(--ad-success-soft) px-1 break-all text-(--ad-success)">
              {change.after}
            </span>
          )}
        </button>
      ))}
    </Column>
  )
}

function JsonColumn({ step }: { step: Step }) {
  const { t } = useTranslation()
  const lines = useMemo(() => jsonLines(step.json), [step.json])
  const changed = useMemo(
    () => new Map((step.changes ?? []).map((change) => [change.path, change])),
    [step.changes]
  )
  const { version } = step

  const lineTone = (line: JsonLine): 'added' | 'changed' | null => {
    if (!step.olderPaths) return null
    if (line.open || line.close) return line.path && !step.olderPaths.has(line.path) ? 'added' : null
    const change = changed.get(line.path)
    if (!change) return null
    return change.before === undefined ? 'added' : 'changed'
  }

  return (
    <Column
      className="w-md"
      header={
        <>
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="font-mono tabular-nums">
              v{step.number}
            </Badge>
            <span className="truncate font-medium">
              {version.user?.name || t('versionHistory.unknownUser')}
            </span>
            {!step.changes && <Badge variant="success">{t('versionHistory.created')}</Badge>}
            <Button
              variant="ghost"
              size="icon-xs"
              className="ml-auto"
              onClick={() => copyToClipboard(JSON.stringify(step.json, null, 2))}
              aria-label={t('common.copyToClipboard')}
            >
              <Copy />
            </Button>
          </div>
          <div className="flex items-center gap-2 text-xs text-(--ad-text-muted)">
            <span title={version.timestamp} className="tabular-nums">
              {formatDateTime(version.timestamp, { dateStyle: 'medium', timeStyle: 'medium' })}
            </span>
            {version.masqueraderUser && (
              <span className="truncate">
                {t('audit.masqueradedBy', { name: version.masqueraderUser.name })}
              </span>
            )}
          </div>
        </>
      }
    >
      <pre data-version={step.number} className="min-w-max py-2 font-mono text-xs leading-5">
        {lines.map((line, i) => {
          const tone = lineTone(line)
          return (
            <div
              key={i}
              data-path={line.close ? undefined : line.path}
              className={cn(
                'flex pr-3',
                tone === 'added' && 'bg-(--ad-success-soft)',
                tone === 'changed' && 'bg-(--ad-warning-soft)'
              )}
            >
              <span
                className={cn(
                  'w-10 shrink-0 border-l-2 border-transparent pr-3 text-right text-(--ad-text-faint) select-none',
                  tone === 'added' && 'border-(--ad-success)',
                  tone === 'changed' && 'border-(--ad-warning)'
                )}
              >
                {i + 1}
              </span>
              <span style={{ paddingLeft: `${line.depth * 2}ch` }}>
                <JsonLineText line={line} />
              </span>
            </div>
          )
        })}
      </pre>
    </Column>
  )
}

function JsonLineText({ line }: { line: JsonLine }) {
  const comma = line.comma ? ',' : ''
  const key = line.key !== undefined && (
    <>
      <span className="text-(--ad-syntax-key)">{JSON.stringify(line.key)}</span>
      {': '}
    </>
  )
  if (line.close) return <>{line.close}{comma}</>
  if (line.open) return <>{key}{line.open}</>
  return (
    <>
      {key}
      <JsonValue value={line.value} />
      {comma}
    </>
  )
}

function JsonValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) return <>[]</>
  if (value && typeof value === 'object') return <>{'{}'}</>
  if (typeof value === 'string') return <span className="text-(--ad-syntax-string)">{JSON.stringify(value)}</span>
  if (typeof value === 'number') return <span className="text-(--ad-syntax-number)">{value}</span>
  return <span className="text-(--ad-syntax-literal)">{String(value)}</span>
}
